import { useState, useCallback, useEffect, useMemo } from 'react';
import type { CategoryProduct } from '../lib/api';

const STORAGE_KEY = 'nfa-comparison-items';
const MAX_ITEMS = 4;
const MIN_ITEMS = 2;

/**
 * Product info stored for comparison
 */
export interface ComparisonProduct {
  id: string;
  name: string;
  category_id: string;
  price_gbp: number;
  weight_g: number;
  tier: 'budget' | 'mid' | 'premium';
  /** Cloudflare Images CDN ID (preferred) */
  cf_image_id?: string | null;
  /** External product image URL (fallback) */
  image_url?: string | null;
  /** Alt text for accessibility and SEO */
  image_alt?: string | null;
}

/**
 * Hook for managing product comparison state
 *
 * Stores selected products in localStorage for persistence across sessions.
 * Provides functions to add, remove, toggle, and clear comparison products.
 */
export function useComparison() {
  const [products, setProducts] = useState<ComparisonProduct[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch {
      // Invalid data, start fresh
    }
    return [];
  });

  // Persist to localStorage whenever products change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    } catch {
      // localStorage might be full or disabled
    }
  }, [products]);

  // Set of product IDs for quick lookup
  const productIds = useMemo(
    () => new Set(products.map((p) => p.id)),
    [products]
  );

  /**
   * Check if a product is in the comparison
   */
  const isInComparison = useCallback(
    (productId: string) => productIds.has(productId),
    [productIds]
  );

  /**
   * Add a product to comparison
   * @returns true if added, false if already exists or at max
   */
  const addProduct = useCallback((product: ComparisonProduct | CategoryProduct) => {
    setProducts((prev) => {
      // Check if already in comparison
      if (prev.some((p) => p.id === product.id)) {
        return prev;
      }
      // Check if at max
      if (prev.length >= MAX_ITEMS) {
        return prev;
      }

      // Normalize CategoryProduct to ComparisonProduct
      // ComparisonProduct has category_id at top level
      const isComparisonProduct = 'category_id' in product && typeof (product as ComparisonProduct).category_id === 'string';

      const normalized: ComparisonProduct = isComparisonProduct
        ? {
            id: product.id,
            name: product.name,
            category_id: (product as ComparisonProduct).category_id,
            price_gbp: product.price_gbp,
            weight_g: product.weight_g,
            tier: product.tier,
            cf_image_id: (product as ComparisonProduct).cf_image_id,
            image_url: (product as ComparisonProduct).image_url,
            image_alt: (product as ComparisonProduct).image_alt,
          }
        : {
            id: product.id,
            name: product.name,
            category_id: '', // CategoryProduct doesn't have category_id exposed
            price_gbp: product.price_gbp,
            weight_g: product.weight_g,
            tier: product.tier,
          };

      return [...prev, normalized];
    });
  }, []);

  /**
   * Remove a product from comparison
   */
  const removeProduct = useCallback((productId: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== productId));
  }, []);

  /**
   * Toggle a product in comparison
   * @returns true if added, false if removed
   */
  const toggleProduct = useCallback((product: ComparisonProduct | CategoryProduct): boolean => {
    const id = product.id;
    if (productIds.has(id)) {
      removeProduct(id);
      return false;
    } else {
      addProduct(product);
      return true;
    }
  }, [productIds, addProduct, removeProduct]);

  /**
   * Clear all products from comparison
   */
  const clearComparison = useCallback(() => {
    setProducts([]);
  }, []);

  /**
   * Get array of product IDs
   */
  const getProductIds = useCallback(() => products.map((p) => p.id), [products]);

  /**
   * Check if comparison can be submitted (has minimum items)
   */
  const canCompare = products.length >= MIN_ITEMS;

  /**
   * Check if more products can be added
   */
  const canAddMore = products.length < MAX_ITEMS;

  /**
   * Number of slots remaining
   */
  const slotsRemaining = MAX_ITEMS - products.length;

  return {
    products,
    productIds,
    isInComparison,
    addProduct,
    removeProduct,
    toggleProduct,
    clearComparison,
    getProductIds,
    canCompare,
    canAddMore,
    slotsRemaining,
    count: products.length,
    maxItems: MAX_ITEMS,
    minItems: MIN_ITEMS,
  };
}
